import { Bone } from "@babylonjs/core/Bones/bone";
import { Skeleton } from "@babylonjs/core/Bones/skeleton";
import type { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Matrix, Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { Scene } from "@babylonjs/core/scene";
import type { RaceHorse, SilkPattern } from "@townsy/contracts";
import { PartBatch, box, cylinder, sphere } from "../../scene/core/meshKit";

/**
 * Townsy Downs horses (ADR-0016): an original low-poly horse with its jockey, one skinned mesh per horse (13 bones:
 * barrel, neck, four two-part legs, tail, jockey) plus its saddle-cloth number on both sides. The gallop is a
 * rotary-gallop leg cycle whose cadence follows the drawn speed; standing horses shift and swish their tails.
 * Local space: facing +z, hooves on y = 0.
 */
enum B {
  root = 0,
  barrel = 1,
  neck = 2,
  flU = 3,
  flL = 4,
  frU = 5,
  frL = 6,
  hlU = 7,
  hlL = 8,
  hrU = 9,
  hrL = 10,
  tail = 11,
  jockey = 12,
}

const BARREL_Y = 1.22;
const LEG_X = 0.16;
const FRONT_Z = 0.55;
const HIND_Z = -0.6;
const JOINT_DY = -0.22;
const UPPER = 0.45;

const REST: Vector3[] = [
  new Vector3(0, 0, 0),
  new Vector3(0, BARREL_Y, 0),
  new Vector3(0, 0.18, 0.66),
  new Vector3(-LEG_X, JOINT_DY, FRONT_Z),
  new Vector3(0, -UPPER, 0),
  new Vector3(LEG_X, JOINT_DY, FRONT_Z),
  new Vector3(0, -UPPER, 0),
  new Vector3(-LEG_X, JOINT_DY, HIND_Z),
  new Vector3(0, -UPPER, 0),
  new Vector3(LEG_X, JOINT_DY, HIND_Z),
  new Vector3(0, -UPPER, 0),
  new Vector3(0, 0.18, -0.8),
  new Vector3(0, 0.32, 0.05),
];
const PARENT = [-1, B.root, B.barrel, B.barrel, B.flU, B.barrel, B.frU, B.barrel, B.hlU, B.barrel, B.hrU, B.barrel, B.barrel];

let skeletonCount = 0;

/** Distance from the horse's origin to its nose: a horse "at" s has its nose at s + NOSE. */
export const HORSE_NOSE = 1.4;

const SKIN = "#E0B48C";
const BREECHES = "#F4F2EC";
const BOOTS = "#1E1E22";
const TACK = "#3A2618";

function darker(hex: string, k: number): string {
  const n = Number.parseInt(hex.slice(1), 16);
  const f = (c: number) => Math.max(0, Math.min(255, Math.round(c * (1 - k))));
  return `#${[(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => f(c).toString(16).padStart(2, "0")).join("")}`;
}

/** The jockey's torso in its silks pattern (torso-local box w x h x d, parented to `node`). */
function silks(scene: Scene, node: TransformNode, body: string, trim: string, pattern: SilkPattern): Mesh[] {
  const w = 0.32;
  const h = 0.46;
  const d = 0.24;
  const out: Mesh[] = [];
  const part = (bw: number, bh: number, bd: number, color: string, x: number, y: number, z: number, rz = 0) =>
    out.push(box(scene, "silk", bw, bh, bd, color, { pos: { x, y, z }, rot: { z: rz }, parent: node }));
  switch (pattern) {
    case "hoops":
      for (let i = 0; i < 4; i++) part(w, h / 4, d, i % 2 === 0 ? body : trim, 0, -h / 2 + h / 8 + (i * h) / 4, 0);
      break;
    case "halves":
      part(w / 2, h, d, body, -w / 4, 0, 0);
      part(w / 2, h, d, trim, w / 4, 0, 0);
      break;
    case "quarters":
      part(w / 2, h / 2, d, body, -w / 4, h / 4, 0);
      part(w / 2, h / 2, d, trim, w / 4, h / 4, 0);
      part(w / 2, h / 2, d, trim, -w / 4, -h / 4, 0);
      part(w / 2, h / 2, d, body, w / 4, -h / 4, 0);
      break;
    case "sash":
      part(w, h, d, body, 0, 0, 0);
      out.push(box(scene, "sash", w + 0.02, 0.1, d + 0.02, trim, { pos: { x: 0, y: 0, z: 0 }, rot: { x: 0.7 }, parent: node }));
      break;
    case "stars":
      part(w, h, d, body, 0, 0, 0);
      for (const [x, y] of [
        [-0.17, 0.1],
        [0.17, -0.08],
        [-0.17, -0.12],
        [0.17, 0.12],
      ] as const)
        part(0.03, 0.08, 0.08, trim, x, y, 0);
      out.push(box(scene, "star", 0.08, 0.08, 0.03, trim, { pos: { x: 0, y: 0.05, z: -d / 2 - 0.01 }, rot: { z: 0.78 }, parent: node }));
      break;
    case "solid":
    default:
      part(w, h, d, body, 0, 0, 0);
      break;
  }
  return out;
}

function buildBody(scene: Scene, id: string, horse: Pick<RaceHorse, "coat" | "silk">): Mesh {
  const b = new PartBatch({ skinned: true });
  const add = (bone: B, m: Mesh) => {
    b.bone = bone;
    b.add(m);
  };
  const coat = horse.coat;
  const mane = darker(coat, 0.55);
  // Barrel: body, chest, rump, saddle cloth and saddle.
  const barrel = cylinder(scene, `${id}_barrel`, { height: 1.3, diameter: 0.62, tessellation: 10 }, coat, { pos: { x: 0, y: BARREL_Y, z: 0 }, rot: { x: Math.PI / 2 } });
  barrel.scaling.set(0.82, 1, 1);
  add(B.barrel, barrel);
  const chest = sphere(scene, `${id}_chest`, 0.64, 8, coat, { pos: { x: 0, y: BARREL_Y + 0.02, z: 0.6 } });
  chest.scaling.set(0.82, 1, 0.9);
  add(B.barrel, chest);
  const rump = sphere(scene, `${id}_rump`, 0.66, 8, coat, { pos: { x: 0, y: BARREL_Y + 0.04, z: -0.6 } });
  rump.scaling.set(0.86, 1, 0.9);
  add(B.barrel, rump);
  b.bone = B.barrel;
  b.box(0.56, 0.42, 0.6, "#F4F2EC", 0, BARREL_Y + 0.1, -0.02);
  b.box(0.42, 0.07, 0.46, TACK, 0, BARREL_Y + 0.33, 0);
  // Neck, head, ears, mane, bridle.
  const neckBase = new Vector3(0, BARREL_Y + 0.18, 0.66);
  const tilt = 0.75;
  add(B.neck, box(scene, `${id}_neck`, 0.26, 0.82, 0.34, coat, { pos: { x: 0, y: neckBase.y + 0.4 * Math.cos(tilt), z: neckBase.z + 0.4 * Math.sin(tilt) }, rot: { x: tilt } }));
  add(B.neck, box(scene, `${id}_mane`, 0.07, 0.78, 0.08, mane, { pos: { x: 0, y: neckBase.y + 0.42 * Math.cos(tilt) + 0.1, z: neckBase.z + 0.42 * Math.sin(tilt) - 0.14 }, rot: { x: tilt } }));
  const top = new Vector3(0, neckBase.y + 0.8 * Math.cos(tilt), neckBase.z + 0.8 * Math.sin(tilt));
  add(B.neck, box(scene, `${id}_head`, 0.22, 0.25, 0.62, coat, { pos: { x: 0, y: top.y - 0.08, z: top.z + 0.2 }, rot: { x: 0.55 } }));
  add(B.neck, box(scene, `${id}_muzzle`, 0.2, 0.2, 0.2, darker(coat, 0.25), { pos: { x: 0, y: top.y - 0.25, z: top.z + 0.45 }, rot: { x: 0.55 } }));
  for (const sx of [-1, 1]) add(B.neck, box(scene, `${id}_ear`, 0.05, 0.13, 0.05, mane, { pos: { x: sx * 0.07, y: top.y + 0.1, z: top.z - 0.02 }, rot: { x: -0.2 } }));
  add(B.neck, box(scene, `${id}_bridle`, 0.24, 0.04, 0.04, TACK, { pos: { x: 0, y: top.y - 0.15, z: top.z + 0.3 }, rot: { x: 0.55 } }));
  // Legs: upper (forearm / gaskin) and lower (cannon + hoof).
  const legs: Array<[B, B, number, number, number]> = [
    [B.flU, B.flL, -LEG_X, FRONT_Z, 0.14],
    [B.frU, B.frL, LEG_X, FRONT_Z, 0.14],
    [B.hlU, B.hlL, -LEG_X, HIND_Z, 0.19],
    [B.hrU, B.hrL, LEG_X, HIND_Z, 0.19],
  ];
  const jointY = BARREL_Y + JOINT_DY;
  for (const [u, l, x, z, thick] of legs) {
    b.bone = u;
    b.box(thick, UPPER + 0.08, thick + 0.05, coat, x, jointY - UPPER / 2, z);
    b.bone = l;
    b.box(0.09, jointY - UPPER - 0.06, 0.1, darker(coat, 0.1), x, (jointY - UPPER) / 2 + 0.03, z);
    b.box(0.12, 0.07, 0.15, "#2A2420", x, 0.035, z + 0.01);
  }
  // Tail.
  add(B.tail, box(scene, `${id}_tail`, 0.1, 0.68, 0.13, mane, { pos: { x: 0, y: BARREL_Y + 0.18 - 0.32, z: -0.86 } }));
  // Jockey: crouched over the withers in the silks.
  const J = BARREL_Y + 0.32;
  b.bone = B.jockey;
  b.box(0.3, 0.16, 0.26, BREECHES, 0, J + 0.12, 0.0);
  for (const sx of [-1, 1]) {
    b.box(0.12, 0.12, 0.38, BREECHES, sx * 0.17, J + 0.1, 0.18);
    b.box(0.1, 0.32, 0.1, BOOTS, sx * 0.23, J - 0.06, 0.32);
  }
  const torso = new TransformNode(`${id}_torsoNode`, scene);
  torso.position.set(0, J + 0.32, 0.2);
  torso.rotation.x = 1.0;
  for (const m of silks(scene, torso, horse.silk.body, horse.silk.trim, horse.silk.pattern)) add(B.jockey, m);
  torso.dispose();
  const shoulder = new Vector3(0, J + 0.32 + 0.23 * Math.cos(1.0), 0.2 + 0.23 * Math.sin(1.0));
  for (const sx of [-1, 1]) {
    add(B.jockey, box(scene, `${id}_arm`, 0.08, 0.08, 0.44, horse.silk.trim, { pos: { x: sx * 0.15, y: shoulder.y - 0.11, z: shoulder.z + 0.19 }, rot: { x: 0.49 } }));
    b.bone = B.jockey;
    b.box(0.07, 0.07, 0.07, SKIN, sx * 0.13, shoulder.y - 0.22, shoulder.z + 0.4);
  }
  add(B.jockey, sphere(scene, `${id}_head`, 0.2, 8, SKIN, { pos: { x: 0, y: shoulder.y + 0.1, z: shoulder.z + 0.1 } }));
  const cap = sphere(scene, `${id}_cap`, 0.23, 8, horse.silk.trim, { pos: { x: 0, y: shoulder.y + 0.15, z: shoulder.z + 0.08 } });
  cap.scaling.set(1, 0.72, 1);
  add(B.jockey, cap);
  b.bone = B.jockey;
  b.box(0.16, 0.025, 0.1, horse.silk.trim, 0, shoulder.y + 0.11, shoulder.z + 0.22);
  // Reins from the hands to the bit.
  b.box(0.3, 0.02, 0.02, TACK, 0, shoulder.y - 0.22, shoulder.z + 0.42);
  const mesh = b.build(`${id}_body`, scene);
  if (!mesh) throw new Error("horse build failed");
  return mesh;
}

/** Number cloths on both sides of the saddle cloth (cell n-1 of the numbers texture), parented to the horse root. */
function buildNumbers(scene: Scene, id: string, n: number, material: StandardMaterial, parent: TransformNode): Mesh {
  const u0 = (n - 1) / 8;
  const u1 = n / 8;
  const x = 0.287;
  const y0 = BARREL_Y - 0.06;
  const y1 = BARREL_Y + 0.27;
  const z0 = -0.24;
  const z1 = 0.2;
  // Right side (+x) reads left to right towards +z; left side (-x) towards -z.
  const positions = [x, y0, z0, x, y0, z1, x, y1, z1, x, y1, z0, -x, y0, z1, -x, y0, z0, -x, y1, z0, -x, y1, z1];
  const uvs = [u0, 0, u1, 0, u1, 1, u0, 1, u0, 0, u1, 0, u1, 1, u0, 1];
  const indices = [0, 2, 1, 0, 3, 2, 4, 6, 5, 4, 7, 6];
  const normals = [1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0];
  const mesh = new Mesh(`${id}_numbers`, scene);
  const vd = new VertexData();
  vd.positions = positions;
  vd.uvs = uvs;
  vd.indices = indices;
  vd.normals = normals;
  vd.applyToMesh(mesh, false);
  mesh.material = material;
  mesh.parent = parent;
  mesh.isPickable = false;
  return mesh;
}

export class HorseRig {
  readonly root: TransformNode;
  private body: Mesh;
  private readonly numbers: Mesh;
  private skeleton: Skeleton;
  private bones: Bone[];
  private key = "";
  private phase: number;
  private idleT: number;
  private gait = 0;
  private readonly q = new Quaternion();
  private readonly t = new Vector3();
  private readonly one = new Vector3(1, 1, 1);

  constructor(
    private readonly scene: Scene,
    readonly n: number,
    private readonly material: StandardMaterial,
    numbersMaterial: StandardMaterial,
    horse: Pick<RaceHorse, "coat" | "silk">,
  ) {
    this.root = new TransformNode(`raceHorse${n}`, scene);
    ({ skeleton: this.skeleton, bones: this.bones } = this.makeSkeleton());
    this.body = this.makeBody(horse);
    this.numbers = buildNumbers(scene, `raceHorse${n}`, n, numbersMaterial, this.root);
    this.phase = n * 0.37;
    this.idleT = n * 1.7;
  }

  private makeSkeleton(): { skeleton: Skeleton; bones: Bone[] } {
    const id = `raceHorse${this.n}_${++skeletonCount}`;
    const skeleton = new Skeleton(`${id}_skel`, `${id}_skel`, this.scene);
    const bones: Bone[] = [];
    REST.forEach((t, i) => {
      const parent = PARENT[i]! >= 0 ? bones[PARENT[i]!]! : null;
      bones.push(new Bone(`${id}_b${i}`, skeleton, parent, Matrix.Translation(t.x, t.y, t.z)));
    });
    return { skeleton, bones };
  }

  private makeBody(horse: Pick<RaceHorse, "coat" | "silk">): Mesh {
    this.key = `${horse.coat}|${horse.silk.body}|${horse.silk.trim}|${horse.silk.pattern}`;
    const mesh = buildBody(this.scene, `raceHorse${this.n}`, horse);
    mesh.material = this.material;
    mesh.parent = this.root;
    mesh.skeleton = this.skeleton;
    mesh.isPickable = false;
    mesh.receiveShadows = true;
    return mesh;
  }

  get meshes(): Mesh[] {
    return [this.body, this.numbers];
  }

  /** New race, new horse in this stall: rebuild the body when the coat or silks changed. Returns true if rebuilt. */
  setLook(horse: Pick<RaceHorse, "coat" | "silk">): boolean {
    const key = `${horse.coat}|${horse.silk.body}|${horse.silk.trim}|${horse.silk.pattern}`;
    if (key === this.key) return false;
    const old = this.body;
    this.body = this.makeBody(horse);
    old.dispose();
    return true;
  }

  private pose(i: B, rx: number, rz = 0, dy = 0): void {
    const bone = this.bones[i]!;
    Quaternion.RotationYawPitchRollToRef(0, rx, rz, this.q);
    this.t.copyFrom(REST[i]!);
    this.t.y += dy;
    Matrix.ComposeToRef(this.one, this.q, this.t, bone.getLocalMatrix());
    bone.markAsDirty();
  }

  /**
   * Place the horse at (x, y, z) facing `heading` and animate for `speed` m/s (0 = standing). The gallop cadence is one
   * stride per ~6.5 m; the legs of a slower horse swing less.
   */
  update(dtSeconds: number, x: number, y: number, z: number, heading: number, speed: number): void {
    const target = Math.min(1, speed / 9);
    this.gait += (target - this.gait) * Math.min(1, dtSeconds * 4);
    const g = this.gait;
    if (speed > 0.2) this.phase += (dtSeconds * Math.max(speed, 3)) / 6.5;
    this.idleT += dtSeconds;
    const TAU = Math.PI * 2;
    const p = this.phase * TAU;
    const bob = g * 0.07 * (0.5 + 0.5 * Math.sin(p));
    const pitch = g * 0.05 * Math.sin(p + 1.1);
    this.root.position.set(x, y + bob, z);
    this.root.rotation.set(pitch, heading, 0);
    const legs: Array<[B, B, number, number]> = [
      [B.hlU, B.hlL, 0.0, 0.8],
      [B.hrU, B.hrL, 0.12, 0.8],
      [B.flU, B.flL, 0.5, 1.15],
      [B.frU, B.frL, 0.62, 1.15],
    ];
    for (const [u, l, off, fold] of legs) {
      const th = p + off * TAU;
      const swing = 0.55 * g * Math.sin(th);
      const bend = fold * g * Math.max(0, -Math.cos(th));
      this.pose(u, swing);
      this.pose(l, bend);
    }
    const idle = 1 - g;
    this.pose(B.neck, g * 0.14 * Math.sin(p + 0.5) + idle * (0.06 * Math.sin(this.idleT * 0.9) + 0.05));
    this.pose(B.tail, g * (0.9 + 0.12 * Math.sin(p * 2)) + idle * 0.08, idle * 0.25 * Math.sin(this.idleT * 1.7));
    this.pose(B.jockey, 0, 0, -bob * 0.6);
    this.pose(B.barrel, 0);
  }

  dispose(): void {
    this.body.dispose();
    this.numbers.dispose();
    this.skeleton.dispose();
    this.root.dispose();
  }
}
